<script setup lang="ts">
import { GoogleSignInButton, type CredentialResponse } from 'vue3-google-signin'
import { decompressFromEncodedURIComponent } from 'lz-string'

const router = useRouter()

definePageMeta({
  layout: false
  // Remove guest-only middleware to allow redirect cookie to work
  // middleware: ['guest-only']
})

const auth = useAuth()
const isReady = ref(false)

// Replace custom loading with UProgress state
const isLoading = ref(false)

// Mouse tracking
const mouse = reactive({ x: 0, y: 0 })
const logo = ref<HTMLElement>()

// Window size using Nuxt's ref
const windowWidth = ref(0)
const windowHeight = ref(0)

// Computed values for animation
const dx = computed(() => Math.abs(mouse.x - windowWidth.value / 2))
const dy = computed(() => Math.abs(mouse.y - windowHeight.value / 2))
const distance = computed(() => Math.sqrt(dx.value * dx.value + dy.value * dy.value))

const size = computed(() => {
  const calculatedSize = Math.max(600 - distance.value / 1.2, 300)
  return calculatedSize
})

const opacity = computed(() => {
  const calculatedOpacity = Math.min(Math.max(size.value / 300, 0.7), 1)
  return calculatedOpacity
})

const logoGradient = computed(() => {
  const rect = logo.value?.getBoundingClientRect()
  const xPos = mouse.x - (rect?.left ?? 0)
  const yPos = mouse.y - (rect?.top ?? 0)
  return `radial-gradient(circle at ${xPos}px ${yPos}px, currentColor 30%, transparent 100%)`
})

// Handle mouse movement
const handleMouseMove = (event: MouseEvent) => {
  mouse.x = event.clientX
  mouse.y = event.clientY
}

// Add redirect handling
const redirectCookie = useCookie('redirect_url')

const handleLogin = async (response?: CredentialResponse) => {
  console.log('🔐 Sign-in attempt:', { response, hasCredential: !!response?.credential })

  if (response?.credential) {
    isLoading.value = true
    console.log('✅ Google credential received, attempting login...')

    try {
      const success = await auth.loginWithGoogle(response.credential)
      console.log('🎯 Login result:', { success })

      if (success) {
        console.log('✅ Login successful, redirecting...')
        // Use redirect cookie if available
        if (redirectCookie.value) {
          const redirectPath = redirectCookie.value
          redirectCookie.value = null // Clear cookie
          await router.push(redirectPath)
        } else {
          await router.push('/samples')
        }
      } else {
        console.error('❌ Login failed: success was false')
      }
    } catch (error) {
      console.error('❌ Login error:', error)
      const toast = useToast()
      toast.add({
        title: 'Login Failed',
        description: 'Please try again',
        color: 'red'
      })
    } finally {
      isLoading.value = false
    }
  } else {
    console.warn('⚠️ No credential in response:', response)
  }
}

// Initialize auth state and window size
onMounted(() => {
  // Debug runtime config
  console.log('🔧 Runtime Config Debug:', {
    googleClientId: runtimeConfig.public.googleClientId,
    hasClientId: !!runtimeConfig.public.googleClientId,
    clientIdLength: runtimeConfig.public.googleClientId?.length || 0
  })

  // Set initial window size
  windowWidth.value = window.innerWidth
  windowHeight.value = window.innerHeight

  // Add window resize listener
  window.addEventListener('resize', () => {
    windowWidth.value = window.innerWidth
    windowHeight.value = window.innerHeight
  })

  // Add mousemove listener
  window.addEventListener('mousemove', handleMouseMove)

  isReady.value = true
})

// Cleanup
onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('resize', () => {})
})

const runtimeConfig = useRuntimeConfig()
</script>

<template>
  <div
    class="min-h-screen w-screen bg-gradient-to-b from-gray-100 dark:from-gray-950 to-gray-500/30 from-80% flex items-center justify-center relative overflow-hidden"
  >
    <!-- Replace custom loading with UProgress -->
    <UProgress
      v-if="isLoading"
      class="absolute top-0 left-0 w-full"
      :indeterminate="true"
      size="xs"
    />

    <!-- Animated background gradient -->
    <div
      class="absolute bg-gray-500/30 rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none blur-3xl"
      :style="{
        opacity,
        left: `${mouse.x}px`,
        top: `${mouse.y}px`,
        width: `${size}px`,
        height: `${size}px`
      }"
    />

    <UCard class="w-[450px] flex flex-col bg-gray-50/90 dark:bg-gray-900/90 backdrop-blur-sm">
      <div
        ref="logo"
        class="header flex items-center gap-4 p-6 border-b border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white"
        :style="{
          maskImage: logoGradient
        }"
      >
        <img src="/favicon.svg" alt="SRXplorer Logo" class="w-16 h-16" />
        <h1 class="text-3xl font-bold">SRXplorer</h1>
      </div>

      <div class="h-[200px] flex items-center justify-center">
        <template v-if="isReady">
          <div style="color-scheme: auto">
            <GoogleSignInButton
              :client-id="runtimeConfig.public.googleClientId || '755795500067-kcni085g2o14a746kvn6qrs5hsja3iae.apps.googleusercontent.com'"
              :itp-support="true"
              :one-tap="true"
              context="use"
              shape="rectangular"
              text="continue_with"
              @success="handleLogin"
              @error="handleLogin"
            />
          </div>
        </template>
        <template v-else>
          <div class="animate-pulse">
            <UButton size="lg" variant="soft" color="gray" :loading="true" class="w-[240px]">
              Sign in with Google
            </UButton>
          </div>
        </template>
      </div>

      <!-- Updated version styling for embossed look -->
      <div
        class="flex justify-center items-center py-3 border-t border-gray-100 dark:border-gray-800"
      >
        <UTooltip text="Application Version">
          <UBadge
            class="text-xs cursor-help text-gray-400 dark:text-gray-500 shadow-[0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_1px_1px_rgba(0,0,0,0.2)] font-mono tracking-wide"
            color="gray"
            variant="soft"
            size="xs"
          >
            v{{ runtimeConfig.public.version || '0.0.0' }}
          </UBadge>
        </UTooltip>
      </div>
    </UCard>
  </div>
</template>
